import { useEffect, useRef } from 'react';
import auth2FAService from '../services/auth/auth2FAService';
import StorageService from '../services/storage/storageService';

/**
 * Hook that monitors token expiration and automatically logs out the user
 * when the token expires.
 */
const useTokenExpirationMonitor = (userToken, onLogout, navigate) => {
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!userToken) {
      // Clear any existing interval when user logs out
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Check token expiry every minute
    const checkTokenExpiry = async () => {
      const isValid = await auth2FAService.checkAndRefreshToken();

      if (!isValid) {
        // Token expired - clear interval and logout
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        // Clear local storage
        StorageService.clearAuthData();

        // Call logout callback
        if (onLogout) {
          onLogout();
        }

        // Navigate to login with expiry message if navigate function is provided
        if (navigate) {
          navigate('/login', {
            state: {
              message: 'Sesiunea ta a expirat. Te rugăm să te autentifici din nou.',
              messageType: 'info'
            }
          });
        } else {
          // Fallback to window.location for navigation outside Router context
          window.location.href = '/login';
        }
      }
    };

    // Initial check
    checkTokenExpiry();

    // Set up interval to check every minute (60 seconds)
    intervalRef.current = setInterval(checkTokenExpiry, 60 * 1000);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [userToken, onLogout, navigate]);

  // Also monitor storage events for logout from other tabs
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'authToken' && !event.newValue) {
        // Token was removed in another tab - logout this tab too
        if (onLogout) {
          onLogout();
        }

        if (navigate) {
          navigate('/login', {
            state: {
              message: 'Ai fost deconectat dintr-o altă fereastră.',
              messageType: 'info'
            }
          });
        } else {
          // Fallback to window.location for navigation outside Router context
          window.location.href = '/login';
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [onLogout, navigate]);

  // Function to get time until expiry
  const getTimeUntilExpiry = () => {
    const expiry = StorageService.get('tokenExpiry');
    if (!expiry) return null;

    const expiryDate = new Date(expiry);
    const now = new Date();
    const diff = expiryDate - now;

    if (diff <= 0) return 0;

    return Math.floor(diff / 1000); // Return seconds
  };

  // Function to format time remaining
  const formatTimeRemaining = () => {
    const seconds = getTimeUntilExpiry();
    if (seconds === null) return '';
    if (seconds <= 0) return 'Expired';

    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);

    if (days > 0) {
      return `${days} ${days === 1 ? 'zi' : 'zile'} ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes} ${minutes === 1 ? 'minut' : 'minute'}`;
    }
  };

  return {
    getTimeUntilExpiry,
    formatTimeRemaining
  };
};

export default useTokenExpirationMonitor;